import { DashboardDivider } from '@g17eco/atoms/divider';
import classNames from 'classnames';
import React from 'react';
import { ConnectionAnswer } from './ConnectionAnswer';
import { ConnectionProps, ConnectionsGroupProps, SurveyConnection, UtrvConnection } from './types';
import { getUnitText } from '@utils/units';
import { NumberScale } from '@g17eco/types/units';
import { DetailsTooltip } from './DetailsTooltip';
import { Variables } from './Variables';
import { CalculationGroupValueType } from '@g17eco/types/utrv-connections';
import { ConnectionsGroupWrapper } from './ConnectionsGroupWrapper';

const getConnectionUnit = ({ connection, utr }: Pick<ConnectionProps, 'connection' | 'utr'>) => {
  if (!connection.valueListCode) {
    return getUnitText({ valueType: utr.valueType, unit: connection.unit, unitType: utr.unitType }, '');
  }

  const column = utr.valueValidation?.table?.columns.find((col) => col.code === connection.valueListCode);
  if (!column) {
    return '';
  }
  return getUnitText({ valueType: column.type, unit: connection.unit, unitType: column.unitType }, '');
};

const isSurveyConnection = (connection: UtrvConnection): connection is SurveyConnection =>
  Boolean('surveyId' in connection);

const isSelectedConnection = ({
  selectedConnection,
  connection,
}: Pick<ConnectionProps, 'selectedConnection' | 'connection'>) => {
  if (selectedConnection && isSurveyConnection(connection) && isSurveyConnection(selectedConnection)) {
    // 1 connection can have multiple survey connections so we need to check the surveyId
    return selectedConnection._id === connection._id && selectedConnection.surveyId === connection.surveyId;
  }

  return selectedConnection?._id === connection._id;
};

const Connection = (props: ConnectionProps) => {
  const {
    connection,
    name,
    utrs,
    integrationUtrs,
    selectedConnection,
    onClickConnection,
    onPopulateInput,
    initiativeId,
    utr,
    surveyScope,
  } = props;

  const numberScale = connection.numberScale === NumberScale.Single ? '' : connection.numberScale;
  const unit = getConnectionUnit({ connection, utr });

  const isTextValueTypeConnection = connection.groupValueType === CalculationGroupValueType.Text;
  const getAnswer = () => {
    if (isTextValueTypeConnection) {
      return connection.value ?? '';
    }
    return `${connection.value} ${numberScale} ${unit}`;
  };

  return (
    <div
      className={classNames('p-1', {
        'cursor-pointer': connection.valueListCode,
        'background-ThemeAccentExtralight': isSelectedConnection({ selectedConnection, connection }),
      })}
      onClick={() => onClickConnection(connection)}
    >
      {name ? (
        <label className='text-ThemeTextMedium mb-2 text-sm'>
          CONNECTION {name} <DetailsTooltip {...props} />
        </label>
      ) : null}
      <div key={connection._id}>
        <ConnectionAnswer
          answerText={String(getAnswer())}
          showPopulationButton={true}
          onPopulateInput={() => onPopulateInput(connection)}
        />
        <Variables
          connection={connection}
          utrs={utrs}
          integrationUtrs={integrationUtrs}
          initiativeId={initiativeId}
          surveyScope={surveyScope}
        />
      </div>
    </div>
  );
};

export const ConnectionsGroup = ({
  title,
  connections,
  displayDetails,
  integrationUtrs,
  utrs,
  utr,
  selectedConnection,
  onClickConnection,
  onPopulateInput,
  initiativeId,
  surveyScope,
}: ConnectionsGroupProps) => {
  return (
    <ConnectionsGroupWrapper title={title}>
      {connections.map((connection, index, connections) => {
        return (
          <React.Fragment key={connection._id}>
            {index ? <DashboardDivider className='my-3' /> : null}
            <Connection
              displayDetails={displayDetails}
              connection={connection}
              name={connections.length === 1 ? undefined : String(index + 1)}
              utrs={utrs}
              integrationUtrs={integrationUtrs}
              selectedConnection={selectedConnection}
              onClickConnection={onClickConnection}
              onPopulateInput={onPopulateInput}
              initiativeId={initiativeId}
              utr={utr}
              surveyScope={surveyScope}
            />
          </React.Fragment>
        );
      })}
    </ConnectionsGroupWrapper>
  );
};
